/**
 * "Proovi vestlust": an AI call campaign's conversation tried out as text (ring R8).
 *
 *   POST /api/admin/crm/calls/ai-outbound/campaigns/:id/rehearse
 *        { regCode | domain, doc?, turns, message } -> { reply }
 *
 * The owner plays the callee; the server answers with the AI's next reply, made
 * with the call's own prompt, model and marker parsing. Nothing rings and
 * nothing is written. A HAND-WRITTEN MIRROR of
 * `apps/api/src/modules/crm-calls/lib/outbound/rehearse.ts`; `aiRehearse.test.ts`
 * holds the limits and the error codes against it.
 *
 * React-free: the conversation is a reducer, tested under plain node.
 */
import { ApiError, api } from "./api";

/** Mirrors `REHEARSE_LIMITS`. */
export const REHEARSE_LIMITS = { turns: 40, text: 600, turnText: 2000 } as const;

/** Every refusal the route names, mirroring `RehearseError`. */
export const REHEARSE_ERRORS = [
  // An alert campaign, and the company has no defect worth a call right now (ring R9).
  "no_alert",
  "unknown_company",
  "invalid_domain",
  "no_domain",
  "flow_invalid",
  "quota",
  "no_gemini_key",
  "model_failed",
] as const;
export type RehearseErrorCode = (typeof REHEARSE_ERRORS)[number];

/** Mirrors `RehearseReply`. */
export interface RehearseReply {
  text: string;
  control: "end" | "handoff" | null;
  step: { label: string; nodeId: string } | null;
  courseSteps: number | null;
  tokensIn: number;
  tokensOut: number;
}

export interface RehearseBody {
  regCode?: string;
  domain?: string;
  doc?: unknown;
  turns: Array<{ role: "callee" | "ai"; text: string }>;
  message: string;
}

export function rehearseCampaign(campaignId: string, body: RehearseBody): Promise<{ reply: RehearseReply }> {
  return api.post(`/admin/crm/calls/ai-outbound/campaigns/${encodeURIComponent(campaignId)}/rehearse`, body);
}

/** One line of the rehearsed conversation after the opening. */
export interface RehearseTurn {
  role: "callee" | "ai";
  text: string;
  /** The AI's lines only: the step it named. */
  step?: { label: string; nodeId: string } | null;
  /** The AI's lines only: it ended the call or asked for a person. */
  control?: "end" | "handoff" | null;
}

export interface RehearseState {
  turns: RehearseTurn[];
  /** What the callee just said, waiting for the AI; null when nobody is waiting. */
  pending: string | null;
  /** How the AI closed the conversation, or null while it goes on. */
  closed: "end" | "handoff" | null;
  /** The last step the AI named, for the map beside the chat. */
  activeNode: string | null;
  /** A catalogue suffix for the last failure, or null. */
  error: string | null;
  /**
   * Which request the conversation waits for. Bumped by every send and every
   * reset, so an answer to a line from before a restart is recognised as stale
   * and dropped instead of landing under the next line.
   */
  seq: number;
}

export type RehearseAction =
  | { type: "send"; text: string }
  | { type: "reply"; reply: RehearseReply; seq: number }
  | { type: "failed"; error: string; seq: number }
  | { type: "reset" };

export function initialRehearse(): RehearseState {
  return { turns: [], pending: null, closed: null, activeNode: null, error: null, seq: 0 };
}

export function reduceRehearse(state: RehearseState, action: RehearseAction): RehearseState {
  switch (action.type) {
    case "send": {
      const text = action.text.trim();
      if (!canSend(state, text)) return state;
      return { ...state, pending: text, error: null, seq: state.seq + 1 };
    }
    case "reply": {
      if (state.pending === null || action.seq !== state.seq) return state;
      const { reply } = action;
      const turns: RehearseTurn[] = [
        ...state.turns,
        { role: "callee", text: state.pending },
        { role: "ai", text: reply.text, step: reply.step, control: reply.control },
      ];
      return {
        turns,
        pending: null,
        closed: reply.control,
        activeNode: reply.step?.nodeId ?? state.activeNode,
        error: null,
        seq: state.seq,
      };
    }
    case "failed":
      if (action.seq !== state.seq) return state;
      // The callee's line was not answered: it goes back to being unsaid, so it can be sent again.
      return { ...state, pending: null, error: action.error };
    case "reset":
      return { ...initialRehearse(), seq: state.seq + 1 };
  }
}

/** Whether `text` may be sent now: something said, nobody waiting, the AI still talking, and room left. */
export function canSend(state: RehearseState, text: string): boolean {
  const said = text.trim();
  return (
    said.length > 0 &&
    said.length <= REHEARSE_LIMITS.text &&
    state.pending === null &&
    state.closed === null &&
    state.turns.length <= REHEARSE_LIMITS.turns
  );
}

/** The conversation is longer than a request may carry: it has to start again. */
export function rehearseTooLong(state: RehearseState): boolean {
  return state.turns.length > REHEARSE_LIMITS.turns;
}

/** The turns a request sends: what was said before the line waiting for an answer, each within the cap. */
export function requestTurns(state: RehearseState): RehearseBody["turns"] {
  return state.turns.map((t) => ({ role: t.role, text: t.text.slice(0, REHEARSE_LIMITS.turnText) }));
}

/** A thrown error as a catalogue suffix under `aiCalls.rehearse.error`. */
export function rehearseErrorKey(err: unknown): string {
  if (err instanceof ApiError && (REHEARSE_ERRORS as readonly string[]).includes(err.message)) return err.message;
  return "failed";
}

/** How the owner named the company by hand: an 8-digit register code, or a domain. Null for neither. */
export function manualCompany(text: string): { regCode: string } | { domain: string } | null {
  const value = text.trim().toLowerCase();
  if (/^\d{8}$/.test(value)) return { regCode: value };
  const domain = value.replace(/^https?:\/\//, "").replace(/^www\./, "").split(/[/?#]/)[0] ?? "";
  return /^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}$/.test(domain) ? { domain } : null;
}
